import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import { parseZoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, canvasOf, card, fade, fill, place, playerColor, zoneBox,
} from '../fx/common';

/**
 * **場を経由せず墓地・手札・除外・デッキへ**（Issue #559。指示書 `../../direction/move.md` の「場を経由せず」）。
 * サーチ（デッキ → 手札）・捨てる（手札 → 墓地。コスト・効果・エンドフェイズの手札の上限も同じ）・落とす（デッキ → 墓地）・
 * 回収（墓地 → 手札）・除外・戻す。**画面の中央を経由して、何のカードかを表で見せる**（ドローと同じ考え方。ユーザー 2026-09-28）。
 *
 * ```
 * 0     出どころ（盤の山・HUD の手札・画面の上の外）に、そこでの大きさで現れる
 * 0〜200 画面の中央へ飛びながら大きくなる（ease_out）
 * 200   中央で止まる（高さ 400）。硬い縁（黒の板 ＋ プレイヤーの色の細い線）、止まった瞬間にカードの形の白い輪が外へ広がって細くなる
 * 500   行き先へ縮みながら飛ぶ（power2_in）
 * 780   着いて消える: ここで盤面を進める（口 `board`。墓地の一番上・HUD の手札の枚数など）
 * 800   終わり（何も残らない）
 * ```
 *
 * 表で見せる。**デッキ（メイン・EX）へ戻す・裏向きの除外だけ裏**（スリーブ）。文字（SEARCH / DISCARD など）は出さない。
 * 複数枚（同じ出どころ → 同じ行き先の move が続く）は前の帯の頭から 120ms で重ねる（口 `overlap`）。
 * 中央で重なる板は uid から決めた少しのずれ・傾きで扇のように見せる（何枚目かは payload から分からないため）。
 */
export const D = 800;
export const TO_CENTER = 200;
export const HOLD_END = 500;
export const ARRIVE = 780;
/** 中央での高さ */
export const CENTER_H = 400;
/** 重ねる間（前の帯の頭から。ドローと同じ） */
export const STACK_MS = 120;
const FADE_IN = 40;
const RING_MS = 140;
const EDGE = 6;

/** 重ねの鍵（同じ出どころ → 同じ行き先の move どうしだけ重ねる） */
export const overlapKeyOf = (payload) => `move:elsewhere:${payload?.fromKind ?? '-'}>${payload?.toKind ?? '-'}`;

/** 裏で見せるか（デッキ・EX デッキへ戻す / 裏向きの除外） */
export function faceDown(payload) {
  const to = payload?.toKind;
  if (to === 'maindeck' || to === 'extra') return true;
  return to === 'removed' && payload?.position?.faceUp === false;
}

/** HUD の手札の k 枚目の場所（`effects/draw.js::landingOf` と同じ並べ方）。返すのは `[x, y, スリーブの幅]` */
export function handSlot(hand, player, k) {
  if (!hand) return null;
  const s = Number.isFinite(hand.scale) ? hand.scale : 1;
  const cw = Number.isFinite(hand.cardW) ? hand.cardW * s : hand.h * CARD_ASPECT;
  if (!Number.isFinite(hand.cardW) || !Number.isFinite(hand.step)) return [hand.x, hand.y, cw];
  const step = hand.step * s;
  const i = Math.max(0, Math.min((hand.max ?? 10) - 1, k));
  const x = player === 1 ? hand.x + hand.w / 2 - i * step - cw / 2 : hand.x - hand.w / 2 + i * step + cw / 2;
  return [x, hand.y, cw];
}

const onScreen = ([x, y], { w, h }) => Number.isFinite(x) && Number.isFinite(y) && x >= 0 && x <= w && y >= 0 && y <= h;

/**
 * **出どころ・行き先の舞台の場所**（「場に出る」と同じ順。ユーザー 2026-09-28）:
 * 1. 盤にそのゾーンがあり画面の中に見えていればそこ（山の上。大きさは写ったゾーンの大きさ）
 * 2. HUD にその場所の部品があれば（今は手札だけ）そこ。行き先の手札は「次の 1 枚」の場所、出どころの手札は並びの真ん中
 * 3. どちらも無ければ画面の上の外
 *
 * @param end 'from' | 'to'
 * @returns {{ x: number, y: number, w: number, rot: number, via: 'field' | 'hud' | 'above' }}
 */
export function spotOf(ctx, end) {
  const { payload } = ctx;
  const canvas = canvasOf(ctx);
  const zone = end === 'from' ? payload?.fromZone : payload?.zone;
  const z = parseZoneKey(zone);
  const f = ctx.stage?.field;
  // 1. 盤のゾーン
  const box = zone ? zoneBox(ctx, zone) : null;
  if (box && onScreen([box.cx, box.cy], canvas)) {
    // 盤に寝ている向き（P2 のゾーンは 180° 回っている）
    const ang = Number(f?.cardAngle?.(zone)) || 0;
    const rot = Math.cos(ang) < 0 ? Math.PI : 0;
    // 写ったゾーンの短い辺 ≒ カードの幅
    return { x: box.cx, y: box.cy, w: Math.max(40, Math.min(box.w, box.h * CARD_ASPECT * 1.1)), rot, via: 'field' };
  }
  // 2. HUD の手札
  if (z?.kind === 'hand') {
    const hand = ctx.stage?.hud?.hand?.(z.player) ?? null;
    if (hand) {
      const n = end === 'from' ? payload?.countBefore?.from : payload?.countBefore?.to;
      const k = end === 'from' ? Math.max(0, ((Number(n) || 1) - 1) / 2) : (Number(n) || 0);
      const s = handSlot(hand, z.player, Math.round(k));
      if (s) return { x: s[0], y: s[1], w: s[2], rot: 0, via: 'hud' };
    }
  }
  // 3. 画面の上の外（盤で写る x があればその真上）
  const at = zone && f?.zonePlane?.(zone) ? zoneBox(ctx, zone) : null;
  const x = at && Number.isFinite(at.cx) ? Math.max(0, Math.min(canvas.w, at.cx)) : canvas.w / 2;
  return { x, y: -CENTER_H * 0.4, w: 120, rot: 0, via: 'above' };
}

/** 中央での場所（uid から決めた少しのずれ・傾き。重なったときに扇に見せる） */
export function centerOf(ctx) {
  const { w, h } = canvasOf(ctx);
  const rand = seeded(`${ctx.payload?.uid ?? ctx.payload?.code ?? ''}:elsewhere`);
  return { x: w / 2 + (rand() - 0.5) * 200, y: h / 2 + (rand() - 0.5) * 40, rot: (rand() - 0.5) * 0.12 };
}

/** 帯の時刻 t の段: 0 = 中央へ、1 = 中央、2 = 行き先へ（それぞれの進み 0 → 1） */
export function phaseAt(t) {
  if (t < TO_CENTER) return { step: 0, k: tween(t, 0, TO_CENTER, 'ease_out') };
  if (t < HOLD_END) return { step: 1, k: seg(t, TO_CENTER, HOLD_END) };
  return { step: 2, k: tween(t, HOLD_END, ARRIVE, 'power2_in') };
}

/** カードが見えているか（着いたら消える） */
export const shownAt = (t) => t >= 0 && t < ARRIVE;

export default defineEffect({
  situation: 'move_elsewhere',
  role: 'effect',
  label: '場を経由せず（中央で見せて行き先へ）',
  color: '#b18cff',
  duration: () => D,
  sounds: () => [{ at: 0, name: 'draw', gain: 0.45 }],
  overlap: ({ payload }) => {
    const key = overlapKeyOf(payload);
    return { key, after: { [key]: STACK_MS } };
  },
  // 着いた瞬間に盤面をイベントの後へ（墓地の一番上・手札の枚数）
  board: () => ({ at: ARRIVE }),
  layers: [
    {
      id: 'elsewhere',
      label: '中央で見せて行き先へ飛ぶカード',
      space: 'screen',
      front: true,
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const back = faceDown(payload);
        const player = parseZoneKey(payload?.zone)?.player ?? payload?.player ?? 0;
        const code = payload?.prev?.code ?? payload?.code ?? null;
        const from = spotOf(ctx, 'from');
        const to = spotOf(ctx, 'to');
        const mid = centerOf(ctx);
        const cw = CENTER_H * CARD_ASPECT;
        const ch = CENTER_H;
        const spin = seeded(`${ctx.seed}:elsewhere`)() < 0.5 ? -1 : 1;

        const g = new THREE.Group();
        add(root, g, 20);
        // 硬い縁: 黒の板（少し大きい）→ カード → プレイヤーの色の細い線（4 辺）
        const plate = fill(ctx, '#0b0c10', cw + EDGE * 2, ch + EDGE * 2);
        add(g, plate, 20);
        const c = card(ctx, { code: back ? null : code, player, faceUp: !back, w: cw, brightness: 1.05 });
        add(g, c.mesh, 21);
        const color = playerColor(ctx, player);
        [0, 1, 2, 3].forEach((i) => {
          const m = fill(ctx, color, 1, 1);
          const horiz = i % 2 === 0;
          const along = horiz ? cw + EDGE * 2 : ch + EDGE * 2;
          place(m, horiz ? 0 : (i === 1 ? 1 : -1) * (cw / 2 + EDGE / 2), horiz ? (i === 0 ? -1 : 1) * (ch / 2 + EDGE / 2) : 0,
            { sx: horiz ? along : EDGE * 0.6, sy: horiz ? EDGE * 0.6 : along });
          add(g, m, 22);
        });
        // 止まった瞬間のカードの形の白い輪（外へ広がって細くなる）
        const ring = [0, 1, 2, 3].map(() => {
          const m = fill(ctx, '#ffffff', 1, 1);
          add(root, m, 23);
          return m;
        });

        const lerp = (a, b, k) => a + (b - a) * k;

        return {
          root,
          update: ({ localMs: t }) => {
            if (!shownAt(t)) {
              fade(g, 0);
              ring.forEach((m) => fade(m, 0));
              return;
            }
            const { step, k } = phaseAt(t);
            let x; let y; let w; let rot;
            if (step === 0) {
              x = lerp(from.x, mid.x, k); y = lerp(from.y, mid.y, k);
              w = lerp(from.w, cw, k);
              rot = lerp(from.rot + spin * 0.35, mid.rot, k);
            } else if (step === 1) {
              // 止まったあと、ほんのわずかに寄る（止めの手応え。位置は動かさない）
              x = mid.x; y = mid.y; w = cw * (1 + 0.02 * (1 - tween(t, TO_CENTER, TO_CENTER + 120, 'expo_out')));
              rot = mid.rot;
            } else {
              x = lerp(mid.x, to.x, k); y = lerp(mid.y, to.y, k);
              w = lerp(cw, to.w, k);
              rot = lerp(mid.rot, to.rot, k);
            }
            place(g, x, y, { s: w / cw, rot });
            // 現れる（出どころで少しずつ濃く）・着く直前に抜ける
            const a = seg(t, 0, FADE_IN) * (1 - seg(t, ARRIVE - 40, ARRIVE));
            fade(g, a);

            // 輪: 中央で止まった瞬間から RING_MS。カードの 4 辺が外へ広がって細くなる
            const ru = seg(t, TO_CENTER, TO_CENTER + RING_MS);
            const on = t >= TO_CENTER && ru < 1;
            const grow = 1 + 0.22 * tween(t, TO_CENTER, TO_CENTER + RING_MS, 'expo_out');
            const thin = 4 * (1 - ru);
            const hw = (cw / 2 + EDGE) * grow;
            const hh = (ch / 2 + EDGE) * grow;
            const cos = Math.cos(mid.rot); const sin = Math.sin(mid.rot);
            const at = (px, py) => [mid.x + px * cos - py * sin, mid.y + px * sin + py * cos];
            ring.forEach((m, i) => {
              const horiz = i % 2 === 0;
              const [px, py] = horiz ? [0, (i === 0 ? -1 : 1) * hh] : [(i === 1 ? 1 : -1) * hw, 0];
              const [rx, ry] = at(px, py);
              place(m, rx, ry, { sx: horiz ? hw * 2 : Math.max(0.001, thin), sy: horiz ? Math.max(0.001, thin) : hh * 2, rot: mid.rot });
              fade(m, on && thin > 0.05 ? 1 : 0);
            });
          },
        };
      },
    },
  ],
}, 'move_elsewhere_effect.js');
